import {
  ChevronDownIcon,
  FilterIcon,
  SearchIcon,
  Trash2Icon,
} from "lucide-react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { formatDocument } from "@/helpers/format"
import { cn } from "@/lib/utils"

type FilterFormData = {
  document?: string
  email?: string
  name?: string
}

export const CustomersFilterForm = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const form = useForm<FilterFormData>({
    defaultValues: {
      document: searchParams.get("document") || "",
      email: searchParams.get("email") || "",
      name: searchParams.get("name") || "",
    },
  })

  const hasParams = Array.from(searchParams.keys()).some(
    (key) => ["document", "email", "name"].includes(key)
  )

  const activeFiltersCount = [
    "document",
    "email",
    "name",
  ].filter((key) => searchParams.get(key)).length

  const handleSubmit = form.handleSubmit((data) => {
    const params = new URLSearchParams(searchParams)
    params.delete("page")

    Object.entries(data).forEach(([key, value]) => {
      if (value && value.trim()) {
        params.set(key, value.trim())
      } else {
        params.delete(key)
      }
    })
    navigate(`?${params.toString()}`)
  })

  const handleResetForm = () => {
    form.reset({
      document: "",
      email: "",
      name: "",
    })
    const params = new URLSearchParams(searchParams)
    params.delete("document")
    params.delete("email")
    params.delete("name")
    params.delete("page")
    navigate(`?${params.toString()}`)
  }

  return (
    <div className="@container">
      {/* Mobile filter toggle */}
      <button
        type="button"
        onClick={() => setIsFilterOpen((prev) => !prev)}
        className="mb-4 flex w-full items-center justify-between rounded-md border bg-background px-4 py-3 text-sm font-medium md:hidden"
      >
        <span className="flex items-center gap-2">
          <FilterIcon size={16} />
          {t("pages.customers.filters.title", "Filtros")}
          {activeFiltersCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
              {activeFiltersCount}
            </span>
          )}
        </span>
        <ChevronDownIcon
          size={16}
          className={cn(
            "transition-transform duration-200",
            isFilterOpen && "rotate-180"
          )}
        />
      </button>

      <div
        className={cn(
          "md:block",
          isFilterOpen ? "block" : "hidden"
        )}
      >
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="grid gap-4 @4xl:grid-cols-[1fr,auto]"
          >
            <div className="grid gap-4 @xl:grid-cols-2 @3xl:grid-cols-3">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input
                        placeholder={t(
                          "pages.customers.filters.name"
                        )}
                        {...field}
                        className="w-full"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="document"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input
                        placeholder={t(
                          "pages.customers.filters.document"
                        )}
                        maxLength={18}
                        {...field}
                        className="w-full"
                        onChange={(e) => {
                          const masked = formatDocument(
                            e.target.value
                          )
                          field.onChange(masked)
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={t(
                          "pages.customers.filters.email"
                        )}
                        {...field}
                        className="w-full"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="flex w-full flex-col justify-start gap-4 @xl:flex-row-reverse @4xl:flex-col">
              <Button
                type="submit"
                className="btn-whitelabel"
              >
                <SearchIcon size={18} className="mr-2" />
                {t("pages.customers.filters.search")}
              </Button>
              {hasParams && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResetForm}
                >
                  <Trash2Icon size={18} className="mr-2" />
                  {t("pages.customers.filters.clear")}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
